import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel, newRecordButtonStyle,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { C360Table, ColumnChooser, ViewIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/vehicles/delivery-notes';

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'DRAFT', label: 'Taslak' },
  { key: 'PRINTED', label: 'Basıldı' },
  { key: 'IN_TRANSIT', label: 'Yolda' },
  { key: 'COMPLETED', label: 'Tamamlandı' },
];

const STATUS_META = {
  DRAFT: { label: 'Taslak', tone: 'neutral' },
  PRINTED: { label: 'Basıldı', tone: 'wait' },
  IN_TRANSIT: { label: 'Yolda', tone: 'wait' },
  COMPLETED: { label: 'Tamamlandı', tone: 'ok' },
};
const statusLabel = (v) => STATUS_META[v]?.label || v || '-';

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.label }));

// Client tarafı kolon filtreleri (backend OData değil; REST + take=500).
const COLUMN_FILTER_META = {
  noteNo: { type: 'text', odataField: 'NoteNo' },
  sdmName: { type: 'text', odataField: 'SdmName' },
  plate: { type: 'text', odataField: 'Plate' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['noteNo', 'sdmName', 'plate', 'deliveryDate'];

const PAGE_SIZE_DEFAULT = 20;

// Popover { op, value } nesnesi ile çalışan client-side filterFn'ler.
const textFilterFn = (row, columnId, fv) => {
  const needle = String(fv?.value ?? '').trim().toLocaleLowerCase('tr-TR');
  if (!needle) return true;
  const cell = String(row.getValue(columnId) ?? '').toLocaleLowerCase('tr-TR');
  switch (fv?.op) {
    case 'equals': return cell === needle;
    case 'startsWith': return cell.startsWith(needle);
    case 'endsWith': return cell.endsWith(needle);
    case 'notContains': return !cell.includes(needle);
    case 'contains':
    default: return cell.includes(needle);
  }
};
const selectFilterFn = (row, columnId, fv) => {
  const v = fv?.value ?? '';
  if (v === '' || v == null) return true;
  return String(row.getValue(columnId) ?? '') === String(v);
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    draftCount: pick(data, 'draftCount', 'DraftCount') ?? 0,
    printedCount: pick(data, 'printedCount', 'PrintedCount') ?? 0,
    inTransitCount: pick(data, 'inTransitCount', 'InTransitCount') ?? 0,
    completedCount: pick(data, 'completedCount', 'CompletedCount') ?? 0,
  };
}

function statusBadgeFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function toIsoStart(d) { if (!d) return ''; try { return new Date(`${d}T00:00:00`).toISOString(); } catch { return ''; } }
function toIsoEnd(d) {
  if (!d) return '';
  try { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + 1); return x.toISOString(); } catch { return ''; }
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function DeliveryNotesPage() {
  usePageMeta('Sevk İrsaliyeleri', 'Araç yüklemesi sonrası bastırılan irsaliyeler');
  const navigate = useNavigate();
  const { canAdd } = usePageActions();

  const [statusFilter, setStatusFilter] = useState('');
  const [plate, setPlate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ draftCount: 0, printedCount: 0, inTransitCount: 0, completedCount: 0 });
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [toast, setToast] = useState(null);

  const [globalFilter, setGlobalFilter] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [columnFiltersState, setColumnFiltersState] = useState([]);
  const [sorting, setSorting] = useState([{ id: 'createdDate', desc: true }]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE_DEFAULT });

  const [selected, setSelected] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (plate.trim()) params.set('plate', plate.trim());
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        draftCount: data.draftCount,
        printedCount: data.printedCount,
        inTransitCount: data.inTransitCount,
        completedCount: data.completedCount,
      });
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, plate, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  // Yeni Sevk İrsaliyesi → tam-sayfa oluşturma ekranı.
  const openCreate = useCallback(() => navigate('/delivery-notes/new'), [navigate]);

  // Satır → tam-sayfa salt-okunur detay (başlık state ile taşınır, satırlar detayda /:id/details ile çekilir).
  const openDetail = useCallback((row) => {
    const id = pick(row, 'id', 'Id');
    navigate(`/delivery-notes/${id}`, { state: { row } });
  }, [navigate]);

  // Normalize edilmiş satırlar (alan adı camelCase/PascalCase fark etmesin).
  const normalizedRows = useMemo(() => rows.map((row) => ({
    id: pick(row, 'id', 'Id'),
    createdDate: pick(row, 'createdDate', 'CreatedDate') ?? null,
    noteNo: pick(row, 'noteNo', 'NoteNo') ?? '',
    sdmName: pick(row, 'sdmName', 'SdmName') ?? '',
    plate: pick(row, 'plate', 'Plate') ?? '',
    status: pick(row, 'status', 'Status') ?? '',
    totalCases: pick(row, 'totalCases', 'TotalCases') ?? null,
    totalUnits: pick(row, 'totalUnits', 'TotalUnits') ?? null,
    detailCount: pick(row, 'detailCount', 'DetailCount') ?? 0,
    deliveryDate: pick(row, 'deliveryDate', 'DeliveryDate') ?? '',
    printedAt: pick(row, 'printedAt', 'PrintedAt') ?? null,
    __raw: row,
  })), [rows]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('createdDate', {
      header: 'Oluşturma', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('noteNo', {
      header: 'İrsaliye No', meta: { filterType: 'text' },
      filterFn: textFilterFn,
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '(Henüz basılmadı)'; },
    }),
    columnHelper.accessor('sdmName', {
      header: 'SDM', meta: { filterType: 'text' },
      filterFn: textFilterFn,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('plate', {
      header: 'Plaka', meta: { filterType: 'text' },
      filterFn: textFilterFn,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      filterFn: selectFilterFn,
      cell: (info) => statusBadgeFor(info.getValue()),
    }),
    columnHelper.accessor('totalCases', {
      header: 'Koli', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? '-',
    }),
    columnHelper.accessor('totalUnits', {
      header: 'Adet', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? '-',
    }),
    columnHelper.accessor('detailCount', {
      header: 'Satır', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openDetail(row.original.__raw); }}><ViewIcon /></button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [openDetail]);

  const table = useReactTable({
    data: normalizedRows,
    columns,
    state: { sorting, columnVisibility, columnFilters: columnFiltersState, globalFilter, pagination },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFiltersState,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: (row, _columnId, value) => {
      const needle = String(value || '').trim().toLocaleLowerCase('tr-TR');
      if (!needle) return true;
      return SEARCH_FIELDS.some((f) => {
        const v = row.original[f];
        return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const filteredCount = table.getFilteredRowModel().rows.length;

  // c360 ColumnFilterPopover -> TanStack columnFilter köprüsü.
  // Popover'ın yeniden doldurması için filtreyi { op, value } nesnesi olarak saklarız;
  // TanStack filterFn'leri ise meta.value alanını okur.
  const columnFiltersObj = useMemo(() => {
    const obj = {};
    columnFiltersState.forEach((f) => { obj[f.id] = f.value; });
    return obj;
  }, [columnFiltersState]);

  const applyColumnFilter = useCallback((columnId, next) => {
    const raw = next?.value ?? '';
    setColumnFiltersState((prev) => {
      const others = prev.filter((f) => f.id !== columnId);
      if (raw === '' || raw == null) return others;
      return [...others, { id: columnId, value: { ...next, value: raw } }];
    });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const clearColumnFilter = useCallback((columnId) => {
    setColumnFiltersState((prev) => prev.filter((f) => f.id !== columnId));
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleExport = () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const exportRows = table.getFilteredRowModel().rows.map((r) => r.original);
      if (exportRows.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const cols = [
        { headerName: 'Oluşturma', valueGetter: ({ row }) => formatDate(row.createdDate) },
        { field: 'noteNo', headerName: 'İrsaliye No' },
        { field: 'sdmName', headerName: 'SDM' },
        { field: 'plate', headerName: 'Plaka' },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
        { field: 'totalCases', headerName: 'Koli' },
        { field: 'totalUnits', headerName: 'Adet' },
        { field: 'detailCount', headerName: 'Satır' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(exportRows, cols, `irsaliyeler_${dateStr}`);
      showToast('success', `${exportRows.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      {STATUS_TABS.map((tab) => (
        <button
          key={tab.key || 'all'}
          type="button"
          onClick={() => setStatusFilter(tab.key)}
          style={{
            ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary),
            padding: '6px 14px', fontSize: 13,
          }}
        >
          {tab.label}
        </button>
      ))}
      <input
        style={{ ...s.filterInput, width: 110 }}
        type="text"
        placeholder="Plaka (sunucu)"
        value={plate}
        onChange={(e) => setPlate(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
      />
      <input style={{ ...s.filterInput, width: 140 }} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
      <input style={{ ...s.filterInput, width: 140 }} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      <input
        style={s.filterInput}
        type="search"
        placeholder="İrsaliye no / SDM / plaka..."
        value={filterInput}
        onChange={(e) => { setFilterInput(e.target.value); setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
      />
      {filterInput && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); setGlobalFilter(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load} disabled={loading}>{loading ? 'Yükleniyor...' : 'Yenile'}</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={pagination.pageIndex + 1}
      totalCount={filteredCount}
      pageSize={pagination.pageSize}
      rowCount={table.getRowModel().rows.length}
      onPageChange={(page) => setPagination((p) => ({ ...p, pageIndex: page - 1 }))}
      onPageSizeChange={(size) => setPagination({ pageIndex: 0, pageSize: size })}
    />
  );

  const headerActions = (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      {badge('Taslak', counts.draftCount, 'gray')}
      {badge('Basıldı', counts.printedCount, 'orange')}
      {badge('Yolda', counts.inTransitCount, 'blue')}
      {badge('Tamamlanan', counts.completedCount, 'green')}
      {canAdd && <button type="button" style={newRecordButtonStyle} onClick={openCreate}>+ Yeni Sevk İrsaliyesi</button>}
    </div>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Sevk İrsaliyeleri"
        subtitle="Araç yüklemesi sonrası bastırılan irsaliyeler. Basım, sevkiyat ve teslimat aşamaları takip edilir."
        actions={headerActions}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFiltersObj}
          onApplyFilter={applyColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => openDetail(row.__raw)}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}

export default DeliveryNotesPage;
